/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { useEffect, type ReactNode } from 'react';
import { AuthProvider as KitAuthProvider, type AuthAccount } from '@vaas/react-ui-kit/auth';

import { setAuthTokenProvider } from '@/lib/apiClient';
import { authMode, msalInstance, tokenProvider } from './msalConfig';
import { mirrorAccessTokenToCookie } from './cookieMirror';
import { buildMockToken } from './mockToken';

/**
 * Signed-in mock user for local dev (mock mode only; ignored under MSAL). Its GMIN defaults to a
 * real dev-DB `user_settings.user_info` row so the language/settings queries fire and saves persist
 * (the backend save is an UPDATE, so a GMIN with no row would silently no-op). Override with
 * `VITE_MOCK_GMIN` to impersonate a different user.
 *
 * <p>No roles here any more: what the mock user may reach is whatever that GMIN has been granted
 * in the dev database, the same as in every other environment. Impersonating a role is a matter of
 * pointing at a GMIN that holds it.
 */
const MOCK_USER: AuthAccount & { gmin: string } = {
  id: 'dev',
  name: 'Dev User',
  username: 'dev@localhost',
  gmin: import.meta.env.VITE_MOCK_GMIN ?? '521426832',
};

/** MSAL when configured; mock session for local dev. Bridges token into axios. */
export function AuthProvider({ children }: { children: ReactNode }) {
  useEffect(() => {
    if (authMode === 'msal') {
      // Bridge the MSAL token into axios and mirror it into the /api cookie so Unity WebGL 3D asset
      // fetches (which cannot set the Authorization header) authenticate against the asset proxy.
      setAuthTokenProvider(async () => {
        const token = await tokenProvider();
        mirrorAccessTokenToCookie(token);
        return token;
      });
      return;
    }
    // Local dev (mock) mode: the backend derives the user (id + MFG role) from a bearer JWT via
    // DisclaimerUtil, so send a mock token carrying the mock user's GMIN instead of no header.
    const mockToken = buildMockToken(MOCK_USER.gmin);
    mirrorAccessTokenToCookie(mockToken);
    setAuthTokenProvider(() => mockToken);
  }, []);

  return (
    <KitAuthProvider
      mode={authMode}
      instance={msalInstance}
      mockUser={MOCK_USER}
      mockInitialSignedIn={authMode === 'mock'}
    >
      {children}
    </KitAuthProvider>
  );
}
